// Page-aware launcher helpers (17 Sep 2026, Barkyn "concierge" spec).
//
// Two things a widget can do differently on ONE page of the store:
//   1. pathLabel()        — a caption beside the launcher ("Fale com um
//                           assistente") on the plan/results page only.
//   2. buildPageContext() — a line appended to the WhatsApp opener that
//                           carries what the page already knows (the dog's
//                           plan answers), so the agent never re-asks.
// Both are pure: no DOM, no React — the loader and the bundle share them, and
// scripts/test-page-context.mjs proves them.

/** Same rule as web_chat_widgets.hidden_paths: each entry is a path FRAGMENT
 *  matched anywhere in the pathname; a trailing "$" means EXACT. Case-
 *  insensitive; trailing slashes ignored on both sides. */
export function pathMatches(entry: string, pathname: string): boolean {
  if (typeof entry !== "string" || !entry.trim()) return false;
  const here = (pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
  const p = entry.trim().toLowerCase();
  if (p.endsWith("$")) return here === (p.slice(0, -1).replace(/\/+$/, "") || "/");
  return here.includes(p.replace(/\/+$/, "") || "/");
}

/** web_chat_widgets.launcher.path_labels: { "<path>": "<caption>" }. The first
 *  matching entry wins; "" when nothing matches (the normal case). */
export function pathLabel(
  labels: Record<string, string> | null | undefined,
  pathname: string,
): string {
  if (!labels || typeof labels !== "object") return "";
  for (const [path, label] of Object.entries(labels)) {
    if (typeof label === "string" && label.trim() && pathMatches(path, pathname)) return label.trim();
  }
  return "";
}

/** web_chat_widgets.quick_actions.context */
export interface PageContextConfig {
  /** Pages it applies to (hidden_paths syntax). */
  paths?: string[] | null;
  /** URL query parameter carrying base64(JSON) — read first when present. */
  param?: string | null;
  /** localStorage key carrying JSON — the fallback (or the only source when a
   *  page strips its query string after reading it, as Barkyn's plan page does). */
  storage_key?: string | null;
  /** Dot path INSIDE the stored JSON to the object with the fields, e.g. "value". */
  storage_path?: string | null;
  /** The line appended to the opener; {field} tokens. Any token with no value
   *  → no line at all (never a half-filled sentence). */
  template?: string | null;
  /** Per-field display map: { gender: { female: "fêmea" } }. */
  values?: Record<string, Record<string, string>> | null;
}

function decodeBase64Utf8(s: string): string {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

function dig(obj: unknown, path: string | null | undefined): unknown {
  if (!path) return obj;
  let cur: unknown = obj;
  for (const key of path.split(".").filter(Boolean)) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/** Read the page's data object per the config: URL param first, storage second. */
export function readPageData(
  cfg: PageContextConfig,
  search: string,
  storage: { getItem(key: string): string | null } | null | undefined,
): Record<string, unknown> | null {
  if (cfg.param) {
    try {
      const raw = new URLSearchParams(search || "").get(cfg.param);
      if (raw) {
        const parsed = JSON.parse(decodeBase64Utf8(raw));
        if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
      }
    } catch {
      /* not this source */
    }
  }
  if (cfg.storage_key && storage) {
    try {
      const raw = storage.getItem(cfg.storage_key);
      if (raw) {
        const parsed = dig(JSON.parse(raw), cfg.storage_path);
        if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
      }
    } catch {
      /* not this source */
    }
  }
  return null;
}

/** The context line for this page, or "" (no matching page, no data, or a
 *  template token without a value). */
export function buildPageContext(
  cfg: PageContextConfig | null | undefined,
  pathname: string,
  search: string,
  storage: { getItem(key: string): string | null } | null | undefined,
): string {
  if (!cfg || typeof cfg !== "object" || !cfg.template) return "";
  const paths = Array.isArray(cfg.paths) ? cfg.paths : [];
  if (!paths.some((p) => pathMatches(p, pathname))) return "";
  const data = readPageData(cfg, search, storage);
  if (!data) return "";
  let missing = false;
  const line = cfg.template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_m, key: string) => {
    const raw = data[key];
    let text = "";
    if (Array.isArray(raw)) text = raw.map(String).filter(Boolean).join(", ");
    else if (raw !== null && raw !== undefined && raw !== "") text = String(raw);
    if (!text) {
      missing = true;
      return "";
    }
    const map = cfg.values?.[key];
    return map && typeof map[text] === "string" ? map[text] : text;
  });
  return missing ? "" : line.replace(/\s+/g, " ").trim();
}

/** The opener with the context on its own line (unchanged when there is none). */
export function withPageContext(message: string, context: string): string {
  const msg = (message || "").trim();
  return context ? (msg ? `${msg}\n${context}` : context) : msg;
}
